import React from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Plus,
  Leaf,
  Droplet,
  Citrus,
  IceCream,
  X,
  Milk,
  MilkOff,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Chip } from "./ui/chip";
import { IconSlider } from "./ui/icon-slider";
import { Carousel } from "./ui/carousel";
import { CompactSliderView } from "./ui/compact-slider-view";

const MilkFullIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
    role="img"
  >
    <path d="M8 2h8" />
    <path d="M9 2v2.789a4 4 0 0 1-.672 2.219l-.656.984A4 4 0 0 0 7 10.212V20a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-9.789a4 4 0 0 0-.672-2.219l-.656-.984A4 4 0 0 1 15 4.788V2" />
    <path d="M7 15a6.472 6.472 0 0 1 5 0 6.47 6.47 0 0 0 5 0V22H7Z" fill="currentColor" stroke="none" />
    <path d="M7 15a6.472 6.472 0 0 1 5 0 6.47 6.47 0 0 0 5 0" />
  </svg>
);

const YogurtBuilder = ({ 
  builderState, 
  builderActions, 
  onAddToCart,
  onAboutClick 
}) => {
  const {
    size,
    flavor,
    customFlavorId,
    toppings,
    acidezIndex,
    texturaIndex,
    grasasIndex,
    acidezOptions,
    texturaOptions,
    grasasOptions,
    MAX_TOPPINGS,
    availableFlavors,
    availableToppings,
    sizes,
  } = builderState;

  const {
    setSize,
    setFlavor,
    setAcidezIndex,
    setTexturaIndex,
    setGrasasIndex,
    toggleTopping,
    isValid,
  } = builderActions;

  const [showHowItWorks, setShowHowItWorks] = React.useState(true);
  const wildcardSelected = React.useMemo(() => {
    if (!availableToppings || !availableToppings.length) {
      return false;
    }
    const wildcardIds = new Set(
      availableToppings
        .filter((topping) => Array.isArray(topping.notWith) && topping.notWith.includes("*"))
        .map((topping) => topping.id)
    );
    return toppings.some((selectedId) => wildcardIds.has(selectedId));
  }, [availableToppings, toppings]);

  return (
    <section className="md:col-span-3 space-y-6">
      {/* "Cómo funciona" card at the top when not at bottom */}
      <AnimatePresence>
        {showHowItWorks && (
          <motion.div
            key="how-it-works-top"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Cómo funciona</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHowItWorks(false)}
                    aria-label="Cerrar guía"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="text-sm text-neutral-600 space-y-2">
                <p>
                  Elegí un <span className="font-medium">tamaño</span> y el
                  <span className="font-medium"> sabor</span>, ajustá{" "}
                  <span className="font-medium">Acidez</span> y{" "}
                  <span className="font-medium">Textura</span>, y pedilo a
                  <span className="font-medium"> tu gusto</span>, combinando hasta{" "}
                  {MAX_TOPPINGS} extras.
                </p>
                <p>La aplicación armará tu encargo en un mensaje de WhatsApp, listo para ser envíado. A partir de ahí seguimos nosotros.</p>
                <p className="text-right">
                  <button
                    onClick={onAboutClick}
                    className="text-blue-600 underline hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Conocé más sobre el yogur y cómo lo hacemos!
                  </button>
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main yogur builder card */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.35 }}
      >
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
              Tamaño de unidad
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pb-12 pt-4 relative">
            {/* Size */}
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <Chip 
                    key={s.id} 
                    active={size === s.id} 
                    onClick={() => setSize(s.id)}
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>

            {/* Flavor */}
            <div className="space-y-1">
              <Label className="text-sm">Sabor</Label>
              <Carousel 
                items={availableFlavors}
                selectedId={flavor}
                onSelect={setFlavor}
                footer={
                  flavor === customFlavorId ? (
                    <div className="space-y-6">
                      <IconSlider
                        label="Acidez"
                        value={acidezIndex}
                        onChange={setAcidezIndex}
                        ticks={acidezOptions}
                        icons={[Leaf, Droplet, Citrus]}
                      />
                      <IconSlider
                        label="Textura"
                        value={texturaIndex}
                        onChange={setTexturaIndex}
                        ticks={texturaOptions}
                        icons={[Leaf, IceCream]}
                      />
                      <IconSlider
                        label="Grasas"
                        value={grasasIndex}
                        onChange={setGrasasIndex}
                        ticks={grasasOptions}
                        icons={[MilkOff, Milk, MilkFullIcon]}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <CompactSliderView
                        label="Acidez"
                        value={acidezIndex}
                        ticks={acidezOptions}
                        icons={[Leaf, Droplet, Citrus]}
                      />
                      <CompactSliderView
                        label="Textura"
                        value={texturaIndex}
                        ticks={texturaOptions}
                        icons={[Leaf, IceCream]}
                      />
                      <CompactSliderView
                        label="Grasas"
                        value={grasasIndex}
                        ticks={grasasOptions}
                        icons={[MilkOff, Milk, MilkFullIcon]}
                      />
                    </div>
                  )
                }
              />
              {flavor === customFlavorId && (
                <p className="text-xs text-neutral-500">
                  Estás personalizando tu sabor.
                </p>
              )}
            </div>

            {/* Toppings */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Agregados (hasta {MAX_TOPPINGS})</Label>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableToppings.map((t) => {
                  const active = toppings.includes(t.id);
                  const maxToppingsReached = !active && toppings.length >= MAX_TOPPINGS;
                  const notWithList = Array.isArray(t.notWith) ? t.notWith : [];
                  const hasWildcardRule = notWithList.includes("*");
                  
                  // Check if this topping conflicts with any selected topping
                  const conflictsWithSelected = !active &&
                    notWithList
                      .filter((conflictId) => conflictId !== "*")
                      .some(conflictId => toppings.includes(conflictId));
                  const wildcardSelfConflict = !active && hasWildcardRule && toppings.length > 0;
                  const blockedByOtherWildcard = !active && wildcardSelected && !hasWildcardRule;
                  
                  const disabled = maxToppingsReached || conflictsWithSelected || wildcardSelfConflict || blockedByOtherWildcard;
                  
                  return (
                    <Chip
                      key={t.id}
                      active={active}
                      onClick={() => toggleTopping(t.id)}
                      disabled={disabled}
                    >
                      <span className="mr-1">{t.label}</span>
                    </Chip>
                  );
                })}
              </div>
            </div>

            {/* CTA spacer to keep content clear of floating button */}
            <div aria-hidden="true" className="h-12" />

            {/* Floating CTA */}
            <div className="pointer-events-none sticky bottom-4 w-full flex justify-end z-30">
              <Button 
                variant="pastelOrange" 
                size="lg" 
                onClick={onAddToCart} 
                disabled={!isValid()}
                className="pointer-events-auto inline-flex gap-2 shadow-2xl shadow-orange-300/80 hover:scale-[1.01] transition-transform duration-200 !bg-orange-500 hover:!bg-orange-600 !text-white !border-orange-500 focus:!ring-orange-500"
              >
                <Plus className="mr-2 h-4 w-4" /> Agregar
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {!showHowItWorks && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHowItWorks(true)}
            aria-label="Mostrar guía Cómo funciona"
          >
            Mostrar "Cómo funciona"
          </Button>
        </div>
      )}

    </section>
  );
};

YogurtBuilder.propTypes = {
  builderState: PropTypes.shape({
    size: PropTypes.string.isRequired,
    flavor: PropTypes.string.isRequired,
    customFlavorId: PropTypes.string.isRequired,
    toppings: PropTypes.arrayOf(PropTypes.string).isRequired,
    acidezIndex: PropTypes.number.isRequired,
    texturaIndex: PropTypes.number.isRequired,
    grasasIndex: PropTypes.number.isRequired,
    acidezOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    texturaOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    grasasOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    MAX_TOPPINGS: PropTypes.number.isRequired,
    availableFlavors: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        desc: PropTypes.string,
        imageUrl: PropTypes.string,
      })
    ).isRequired,
    availableToppings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        notWith: PropTypes.arrayOf(PropTypes.string),
      })
    ).isRequired,
    sizes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  builderActions: PropTypes.shape({
    setSize: PropTypes.func.isRequired,
    setFlavor: PropTypes.func.isRequired,
    setAcidezIndex: PropTypes.func.isRequired,
    setTexturaIndex: PropTypes.func.isRequired,
    setGrasasIndex: PropTypes.func.isRequired,
    toggleTopping: PropTypes.func.isRequired,
    resetBuilder: PropTypes.func.isRequired,
    getCurrentProduct: PropTypes.func.isRequired,
    isValid: PropTypes.func.isRequired,
  }).isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onAboutClick: PropTypes.func.isRequired,
};

export default React.memo(YogurtBuilder);
